---
en:
  notifications:
    admin:
      channels:
        email: Email
        portal_toast: Portal toast
      composer:
        fields:
          all_users: All users
          body: Body
          channels: Channels
          community_post: Link to community post
          context_url: Context URL (optional)
          direct_users: Direct recipients
          features: Feature flags
          portfolios: Portfolios
          scope: Scope
          send: Send notification
          severity: Severity
          title: Title
        hints:
          all_users_caption: Use carefully for broad announcements.
          all_users_tooltip: Sends to every active portal user in your current scope.
          channels: Select delivery channels and audience segments before sending.
          context: Add an optional link to explain why this notification matters.
          layout_intro: Build the message first, then confirm recipients and channels before sending.
          targeting: Combine direct users, portfolios, and feature flags to control who receives this notification.
        preview:
          direct_users: Direct users
          features: Feature flags
          portfolios: Portfolios
        preview_headers:
          email: Email
          recipient: Recipient
        recipient_notice:
          all_users: Sending to all portal users.
          direct_summary: "{count} direct"
          feature_summary: "{count} via {feature}"
          loading: Calculating recipient count...
          portfolio_summary: "{count} from {name}"
          select: Select at least one recipient via direct users, portfolios, or feature flags.
          separator: " • "
          summary: Sending to {count} recipients{details}
        sections:
          audience: Audience
          channels: Channels & Segments
          context: Message Context
          delivery: Delivery
          message: Message Content
          preview: Recipient Preview
          review: Review & Send
          settings: Notification Settings
          targeting: Direct Targeting
        send_summary:
          channels: 'Channels: {value}'
          no_channels: No channels selected
          recipients: 'Recipients: {count}'
          scope: 'Scope: {value}'
          scope_all_users: All users
          scope_selected: Selected recipients
          severity: 'Severity: {value}'
          title: Send summary
        severities:
          error: Error
          info: Info
          success: Success
          warning: Warning
        title: Compose Notification
      delivery_status_reasons:
        email_channel_disabled: Email channel disabled
        invalid_email: Invalid email format
        missing_recipient_email: Missing recipient email
        missing_user_mapping: Missing user mapping
        portal_toast_channel_disabled: Portal toast channel disabled
        portal_toast_opted_out: User disabled in-app popups
        whitelist_block: Blocked by allowlist
      delivery_statuses:
        blocked_by_kill_switch: Blocked by kill switch
        dropped_by_whitelist: Dropped by allowlist
        sent: Sent
        skipped_invalid_recipient: Skipped (invalid recipient)
        suppressed_by_user_preference: Suppressed by user preference
      health:
        alert: Heavy activity detected. Review recent deliveries and consider pausing a channel.
        no_activity: No recent activity
        per_channel_title: Deliveries in the last {minutes} min
        range_15m: Last 15 min
        range_1h: Last 1 hour
        range_24h: Last 24 hours
        range_7d: Last 7 days
        range_label: Activity window
        top_recipients_title: Top recipients ({minutes} min)
      reports:
        channel_breakdown_title: Channel breakdown
        channel_series_label: Deliveries
        delivery_reason_empty: No delivery failure reasons
        delivery_reason_title: Delivery failure reasons
        delivery_status_all: All statuses
        delivery_status_empty: No delivery status data
        delivery_status_label: Delivery status
        delivery_status_title: Delivery status breakdown
        errors:
          preview: Failed to preview recipients
          refresh: Failed to refresh notification report
          trigger: Failed to trigger notification
        failed_job_alerts_channel: 'Email channel: {enabled}'
        failed_job_alerts_email_disabled: email channel disabled for this user
        failed_job_alerts_empty: No opted-in recipients yet
        failed_job_alerts_title: Failed background job email recipients
        generated_at: Generated at {timestamp}
        headers:
          channels: Channels
          delivery_status: Status
          occurrences: Occurrences
          severity: Severity
          title: Title
          user: User
          when: When
        heading: Portal Notification Report
        no_channel_data: No data
        quick_tests: Quick Test Notifications
        read_label: Read
        read_unread_series_label: Notifications
        read_unread_title: Read vs Unread
        recent_notifications_title: Recent notifications
        test_default_body: Generated from the admin notification report at %{timestamp}
        test_default_title: "%{severity} notification test"
        test_event_type_label: Test event type
        test_events:
          email: Email delivery test
          portal: Portal toast test
        top_events_header_count: Count
        top_events_header_event: Event
        top_events_title: Top events
        top_recipients_header_count: Count
        top_recipients_header_user: User
        top_recipients_title: Top recipients
        totals_line: 'Active: {active} • Unread: {unread}'
        totals_title: Totals
        trigger_label_prefix: Trigger
        trigger_label_suffix: ''
        unknown_event: Unknown
        unread_label: Unread
      safeguards:
        confirm_disable: Disable {channel} notifications?
        disabled: Disabled
        enabled: Enabled
        never_disabled: Never disabled
        recipient_hint: Changes apply instantly to new deliveries.
        recipient_prompt: Select a user to manage their channels.
        recipient_search: Find a recipient
        recipient_subtitle: Pause channels for a single user in emergencies.
        recipient_title: Recipient overrides
        recipient_unavailable: Admin override endpoints are unavailable in this environment.
        refresh: Refresh safeguards
        subtitle: Kill switches pause delivery instantly per channel.
        title: Delivery safeguards
      tabs:
        compose: Compose & Send
        reports: Reports & Tests
      tray:
        admin_link: Open Notifications Hub
        admin_link_hint: Manage preferences and detailed history inside the hub.
        dismiss: Dismiss
        empty: No notifications yet.
        mark_read: Mark read
        more_hint: "... +{count} more —"
        more_link: View in Notifications Hub
        no_settings_user: Notification settings are unavailable for this session.
        settings_link: Manage notification settings
        similar: deliveries
        tabs:
          notifications: Notifications
          settings: Settings
        view: View
    email:
      generic:
        brand_heading: "%{brand}"
        button: Open portal
        footer: You are receiving this email because notifications are enabled for your account.
        meta:
          attempts: Attempts
          failed_at: Failed at
          job_class: Job class
          job_id: Job ID
          queue: Queue
          run_at: Scheduled for
          sent_at: Sent at
        sections:
          details: Delivery details
          error_excerpt: Recent error
          handler_excerpt: Job payload
        title: You have a new notification
    hub:
      actions:
        dismiss_all: Clear all
        load_more: Load more
        mark_all_read: Mark all read
        refresh: Refresh feed
      errors:
        notifications_fetch: Unable to load notifications.
        notifications_update: Unable to update notifications. Please try again.
        preference_update: Unable to update preferences. Please try again.
      filters:
        range_30d: Last 30 days
        range_7d: Last 7 days
        range_all: All time
        range_label: Period
        severity_all: All
        severity_attention: Needs attention
        severity_label: Show
        severity_success: Success
        severity_updates: Updates
        visibility_all: All items
        visibility_label: Status
        visibility_needs_action: Needs action
        visibility_open: Open items
        visibility_unread: Unread only
      inbox:
        empty: You're all caught up. New notifications will appear here.
        groups:
          today: Today
          undated: No timestamp
          yesterday: Yesterday
        hero_body: Review the latest alerts, keep tabs on outstanding items, and jump into details from a single view.
        hero_title: Stay in control
        meta:
          just_now: just now
          last_refreshed: 'Last updated: {value}'
          never_refreshed: Not synced yet
          range_label: Time span
          total_label: Total alerts
          unread_label: Unread
        subtitle: Manage your in-portal alerts and keep your tray tidy.
        title: My notifications
      preferences:
        coming_soon: Coming soon
        delivery_section_title: Delivery channels
        email: Email alerts
        email_hint: Send a mirrored copy to your inbox for redundancy.
        failed_job_alerts: Failed job emails
        failed_job_alerts_hint: Admin and developer only. Opt in to receive failed background job emails.
        notice: Critical account emails (e.g. password resets) remain enabled even if email alerts are toggled off.
        sms: SMS alerts
        sms_hint: SMS alerts will arrive alongside portal notifications soon.
        subtitle: Choose how you want to be notified. Some transactional emails remain mandatory.
        system_opt_ins_hint: Operational notification subscriptions that are not standard delivery channels.
        system_opt_ins_title: System opt-ins
        title: Delivery channels
        toast: In-app alerts
        toast_hint: In-app alerts control real-time popup banners; inbox and bell items are still saved.
      tabs:
        inbox: My notifications
    system:
      failed_job:
        body: 'A background job has failed (job #%{job_id}, %{attempts} attempts).'
        cta: Review in job monitor
        preview: Job %{job_id} failed after %{attempts} attempts.
        subject: 'Background job #%{job_id} failed'
        title: Background job failed
